import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { Labels, RfqModaltabs, RfqModalTemplate as template } from './rfq-modal.template';
import { RfqModalStyles as styles } from './rfq-modal.styles';
import { FileUpload } from '../../common/file-upload/file-upload';
import { FieldValidation, IRfqInsert, ObjectValidation, ValidationError } from '../../../types';
import { RfqForm } from './rfq-form/rfq-form';
import { RightSideModal } from '../../right-side-modal/right-side-modal';
import { logger } from '@genesislcap/foundation-comms';
import { ValidationColumnConfig } from '../../common/validation-grid/validation-grid';
import { IRfqService } from '../../../services/rfq.service';
import moment from 'moment';
import { notify, notifyError } from '../../../utils/notify';

const name = 'rfq-modal';

export enum rfqFieldsType {
  VPPA_STATUS = 'string',
  TECHNOLOGY = 'string',
  CONTRACT_TYPE = 'string',
  COUNTRY_NAME = 'string',
  STATE_NAME = 'string',
  CITY_NAME = 'string',
  TARGET_COD = 'date',
  GUARANTEED_COD = 'date',
  REQUESTED_VOLUME = 'number',
  FIXED_PRICE_LOWER_BOUND = 'number',
  FIXED_PRICE_UPPER_BOUND = 'number',
  ISO = 'string',
  SETTLEMENT_POINT = 'string',
  SETTLEMENT_INDEX_STRING = 'string',
  TENOR_START_DATE = 'date',
  TENOR_MONTHS = 'number',
  RFQ_NEGATIVE_PRICING_METHOD = 'string',
  ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS = 'string',
  UPSIDE_SHARING_FIXED_PRICE = 'number',
  SELLER_ALLOCATED_UPSIDE_PERCENTAGE = 'number',
  COLLAR_CAP_PRICE = 'number',
  COLLAR_FLOOR_PRICE = 'number',
  BUYER_UPSIDE_ALLOCATION_PRICE = 'number',
  BUYER_UPSIDE_ALLOCATION_FLOOR_PRICE = 'number',
  APPLICABLE_TRACKING_SYSTEM = 'string',
  RFQ_REC_DELIVERY_METHODOLOGY = 'string',
  RFQ_PERFORMANCE_GUARANTEE_METHOD = 'string',
  RFQ_APPLICABLE_REC_PROGRAM = 'string',
  EXPECTED_SETTLEMENT_MAX = 'number',
  EXPECTED_SETTLEMENT_MIN = 'number',
  DOWNSIDE_RISK_MAX = 'number',
  DOWNSIDE_RISK_MIN = 'number',
  NODAL_LME_HISTORIC_MAX = 'number',
  NODAL_LME_HISTORIC_MIN = 'number',
  IMPLIED_CARBON_PRICE_MAX = 'number',
  IMPLIED_CARBON_PRICE_MIN = 'number',
  RFQ_REFERENCE = 'string',
  CAPACITY = 'number',
  FIXED_PRICE = 'number',
}

@customElement({
  name,
  template,
  styles,
})
export class RfqModal extends FASTElement {
  @IRfqService rfqService: IRfqService;
  @observable show: boolean = false;
  @observable selectedTab: RfqModaltabs = RfqModaltabs.FORM;
  @observable title: Labels | string = Labels.TITLE;
  @observable isFileConfirmationState: boolean = false;
  @observable width: string = '375px';
  @observable file: any = undefined;
  @observable isFormValid: boolean;
  @observable isUpdateModal: boolean = false;
  @observable selectedRfqTicker: string;

  @observable correctRfqObjectsUploaded: Array<ObjectValidation> = [];
  @observable incorrectRfqObjectsUploaded: Array<ObjectValidation> = [];
  @observable rfqColumns: Array<ValidationColumnConfig> = [];

  @observable rfqForm: RfqForm;
  @observable rfqFormUpdate: RfqForm;
  @observable rfqModal: RightSideModal;

  public fileUpload: FileUpload;
  public autoExecString: string = 'MINI_POST_BUILD_VPPA_AUTO_EXECUTED';

  constructor() {
    super();
    this.loadColumns();
  }

  private loadColumns() {
    this.rfqColumns.push({ fieldName: 'TICKER', headerName: 'Ticker', flex: 3 });
    this.rfqColumns.push({ fieldName: 'TECHNOLOGY', headerName: 'Type', flex: 2 });
    this.rfqColumns.push({
      fieldName: 'SETTLEMENT_POINT',
      headerName: 'Settlement Point',
      flex: 3,
    });
    this.rfqColumns.push({ fieldName: 'TARGET_COD', headerName: 'COD', flex: 3 });
    this.rfqColumns.push({
      fieldName: 'FIXED_PRICE_LOWER_BOUND',
      headerName: 'Price Lower Bound',
      flex: 3,
    });
    this.rfqColumns.push({
      fieldName: 'FIXED_PRICE_UPPER_BOUND',
      headerName: 'Price Upper Bound',
      flex: 3,
    });
  }
  // update will be done here
  public async openModal(isUpdate: boolean, rfqId: string, rfqTicker?: string) {
    this.isUpdateModal = isUpdate;
    this.show = true;
    rfqTicker ? (this.selectedRfqTicker = rfqTicker) : null;

    setTimeout(() => {
      if (isUpdate) {
        this.title = Labels.UPDATE_TITLE + ' ' + rfqTicker;
        this.rfqFormUpdate?.loadData(rfqId, isUpdate);
      } else {
        if (rfqId) {
          this.title = Labels.COPY_TITLE + ' ' + rfqTicker;
          this.rfqForm?.loadData(rfqId, isUpdate);
        } else {
          this.title = Labels.ADD_BUTTON;
          this.rfqForm?.loadData(null, isUpdate);
        }
      }
    }, 100);
  }

  public selectTab(tab: RfqModaltabs) {
    this.selectedTab = tab;
  }

  closeModal() {
    this.title = Labels.TITLE;
    this.width = '375px';
    this.fileUpload?.cleanData();
    this.isFileConfirmationState = false;
    this.file = undefined;
    this.correctRfqObjectsUploaded?.splice(0);
    this.incorrectRfqObjectsUploaded?.splice(0);
    this.rfqForm.clearForm();
    this.rfqFormUpdate?.clearForm();
    this.isUpdateModal = false;
    this.show = false;
  }

  clearForm(): any {
    this.rfqForm.cleanClassValues();
    this.rfqFormUpdate?.reloadRfqData();
  }

  public submitForm() {
    if (this.isUpdateModal) {
      const formData = this.rfqFormUpdate.getFullFormData();

      this.rfqService.eventRfqUpdate(formData).then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            this.rfqModal.closeDialog();
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response);
          }
        },
        (error) => {
          notifyError(error);
          logger.error(error);
        },
      );
    } else {
      const formData = this.rfqForm.getFormData();

      this.rfqService.eventRfqInsert(formData).then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            this.rfqModal.closeDialog();
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response);
          }
        },
        (error) => {
          notifyError(error);
          logger.error(error);
        },
      );
    }
  }

  public async submitFile() {
    this.fileUpload.setEndpoint('file-handler/upload-rfq');
    if (this.file) {
      const response = await this.rfqService.sendRfqCsv(
        this.file,
        this.fileUpload.getEndpointUrl(this.fileUpload.endpoint),
      );
      this.correctRfqObjectsUploaded = response.objects?.filter(
        (object) => !object.objectHasProblem,
      );
      this.incorrectRfqObjectsUploaded = response.objects?.filter(
        (object) => object.objectHasProblem,
      );
      this.isFileConfirmationState = true;
      this.width = '525px';
      this.title = Labels.CONFIRM_RFQ_UPLOAD_TITLE;
    }
  }

  public async insertRfqsList() {
    if (this.isFileConfirmationState && !this.incorrectRfqObjectsUploaded.length) {
      const rfqsResponseList = [];
      this.correctRfqObjectsUploaded.forEach((rfq) => {
        const rfqResponse: IRfqInsert = {
          RFQ_STATUS: '',
          TECHNOLOGY: '',
          CONTRACT_TYPE: '',
          COUNTRY_NAME: '',
          STATE_NAME: '',
          CITY_NAME: '',
          TARGET_COD: 0,
          GUARANTEED_COD: 0,
          REQUESTED_VOLUME: 0,
          FIXED_PRICE_LOWER_BOUND: 0,
          FIXED_PRICE_UPPER_BOUND: 0,
          ISO: '',
          SETTLEMENT_POINT: '',
          SETTLEMENT_INDEX_STRING: '',
          TENOR_START_DATE: 0,
          TENOR_MONTHS: 0,
          RFQ_NEGATIVE_PRICING_METHOD: '',
          APPLICABLE_TRACKING_SYSTEM: '',
          RFQ_REC_DELIVERY_METHODOLOGY: '',
          RFQ_PERFORMANCE_GUARANTEE_METHOD: '',
          RFQ_APPLICABLE_REC_PROGRAM: '',
          CASCADE_COMPANY_ID: '',
          RFQ_REFERENCE: '',
          EXPECTED_SETTLEMENT_MAX: 0,
          EXPECTED_SETTLEMENT_MIN: 0,
          DOWNSIDE_RISK_MAX: 0,
          DOWNSIDE_RISK_MIN: 0,
          NODAL_LME_HISTORIC_MAX: 0,
          NODAL_LME_HISTORIC_MIN: 0,
          IMPLIED_CARBON_PRICE_MAX: 0,
          IMPLIED_CARBON_PRICE_MIN: 0,
          CARBON_FREE_ENERGY_UPPER_BOUND: 0,
          CARBON_FREE_ENERGY_LOWER_BOUND: 0,
          RFQ_CREDIT_PROFILE: '',
          BUYER_POST_COD_COLLATERAL: 0,
          FIXED_PRICE: 0,
          CAPACITY: 1,
          PRODUCT_TYPE: '',
          FLOOR_QUANTITY: 0,
        };
        rfq.fields.map((field) => {
          return this.updatedRfqField(field, rfqResponse);
        });
        rfqsResponseList.push(rfqResponse);
      });

      this.rfqService.eventRfqsInsert(rfqsResponseList).then(
        (response) => {
          if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
            this.rfqModal.closeDialog();
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response.ERROR);
          }
        },
        (error) => {
          notifyError(error);
          logger.error(error);
        },
      );
    }
  }

  updatedRfqField(field: FieldValidation, rfqResponse: IRfqInsert) {
    if (rfqFieldsType[field.fieldName] === 'string') {
      rfqResponse[field.fieldName] = field.fieldValue;
    } else if (rfqFieldsType[field.fieldName] === 'number') {
      rfqResponse[field.fieldName] = Number(field.fieldValue);
    } else if (rfqFieldsType[field.fieldName] === 'date') {
      rfqResponse[field.fieldName] = new Date(field.fieldValue);
    }
  }

  public clearFileArea() {
    if (this.file) {
      this.fileUpload?.cleanData();
      this.isFileConfirmationState = false;
      this.file = undefined;
    }
  }

  public cancelFileUpload() {
    this.closeModal();
    this.rfqModal.closeDialog();
  }

  public fileSelected(file: any) {
    this.file = file;
    if (this.isFileConfirmationState) {
      this.submitFile();
    }
  }
}
